import type { Route } from './+types/resource.geo'

// Per-viewer geo — never shared-cache (matches resource.zip-search.ts). The
// landing HTML is shared-cached with no header in the cache key, so geo must
// never be read during SSR of a cached page; this dedicated no-store route is
// fetched client-side after hydration instead (see SiteHeader).
// `noindex` as a header rather than a `<meta>` tag because there is no document to put one
// in — this route has no component, so it answers with JSON. It is the replacement for the
// `Disallow: /resource/` that robots.txt used to carry: that rule kept Googlebot from
// fetching this endpoint at all, which broke rendering site-wide (VIT-17952, see robots.txt).
// Crawlable-and-noindexed is the combination we actually want, and it only works this way
// round — a disallowed URL can still be indexed, since the directive is never read.
export const headers: Route.HeadersFunction = () => ({
  'Cache-Control': 'private, no-store',
  'X-Robots-Tag': 'noindex',
})

export type GeoLocation = {
  city: string | null
  region: string | null // subdivision code, e.g. "NY"
  postalCode: string | null
  country: string | null // ISO alpha-2, e.g. "US"
}

// CloudFront percent-encodes non-ASCII characters in its viewer-location header
// values per RFC 3986, so a city like "San José" arrives as "San Jos%C3%A9".
// Decode back to the display form; a malformed sequence (lone `%`) would make
// decodeURIComponent throw, so fall back to the raw value rather than error the
// loader. Null passes straight through. Only `city` needs this — `region` is an
// ASCII subdivision code, `country` is a 2-letter code, `postalCode` is ASCII.
const decodeHeader = (value: string | null): string | null => {
  if (value == null) return value
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

// CloudFront forwards these viewer geo headers (Stage 1, VIT-17230) via a custom
// origin-request policy. Absent that policy — or locally — they're simply null,
// and SiteHeader falls back to its default location. No SigV4 backend call here
// (data comes straight off request headers), so this loader is trivial and never
// throws (contrast the ZIP/taxonomy loaders which proxy the server-only client).
// `Headers.get` is case-insensitive, matching CloudFront's lowercased names.
//
// There is deliberately no dev mock here. This used to return a hardcoded
// Houston under `pnpm start-with-mocks`, which meant clearing the localStorage
// override in dev landed on a US city instead of the all-null geo a viewer with
// no CloudFront headers actually gets — so the unresolved-geo path ("Near you",
// and a Search button that can't build a location slug) was unreachable locally
// and dev diverged from production. To view the app as another location, set the
// override — the /__geo page has presets covering US, non-US and no-region cases
// (see GEO_OVERRIDE_KEY in use-geo.ts).
export const loader = ({ request }: Route.LoaderArgs): { geo: GeoLocation } => {
  const h = request.headers

  return {
    geo: {
      city: decodeHeader(h.get('cloudfront-viewer-city')),
      region: h.get('cloudfront-viewer-country-region'),
      postalCode: h.get('cloudfront-viewer-postal-code'),
      country: h.get('cloudfront-viewer-country'),
    },
  }
}

// Derive the viewer's display location from geo — today the home hero's location
// line, and the location named in UsOnlyNotice. When there's no city (loading, or
// geo unavailable) return null so the caller can fall back ("Near you") rather
// than render a misleading default. Pure and exported so it's unit-testable
// without driving a live fetcher (Atlas Web unit tests run in Node — no browser
// mode). Returning null on the pre-fetch render also keeps SSR and the initial
// client render identical (both render nothing → no hydration mismatch).
//
// The qualifier after the city differs by country, because the useful one does:
//
// * US viewers get the subdivision code and, when one resolved, the ZIP —
//   "Austin, TX 73301". That's the familiar 2-letter state plus the ZIP the
//   search form will submit with, so the label names the same
//   city--region--zip search the results page heads (VIT-18219).
// * Everyone else gets the country code — "Auckland, NZ". Their
//   `cloudfront-viewer-country-region` is the raw ISO 3166-2 subdivision, which
//   is obscure (NZ "AUK", which just echoes the city), numeric (JP "13"), or
//   blank (Singapore), whereas the country is the fact that actually matters
//   here: it's why the search won't work (see isOutsideUs and UsOnlyNotice).
//
// Puerto Rico lands in the second branch and reads "San Juan, PR" — its own ISO
// country code happens to be the qualifier you'd want anyway.
export const formatGeoLabel = (geo: GeoLocation | undefined): string | null => {
  // Treat an empty/whitespace-only city as unresolved: CloudFront can send the
  // header present-but-blank, which would otherwise render a stray ", REGION".
  const city = geo?.city?.trim()
  if (city == null || city === '') return null

  const country = geo?.country?.trim()
  const region = geo?.region?.trim()
  const postalCode = geo?.postalCode?.trim()

  if (country?.toUpperCase() === 'US') {
    if (region == null || region === '') return city
    // Only a 5-digit code is appended: anything else can't build the slug, so
    // the label would name a ZIP the search can't use.
    return postalCode != null && /^\d{5}$/.test(postalCode) ? `${city}, ${region} ${postalCode}` : `${city}, ${region}`
  }

  // An unresolved country leaves nothing sensible to append, so the bare city
  // stands alone rather than rendering a trailing comma.
  return country != null && country !== '' ? `${city}, ${country.toUpperCase()}` : city
}

// ISO 3166-1 country codes we have provider data for. Puerto Rico is its own
// alpha-2 country code, so CloudFront reports a San Juan viewer as `PR`, not
// `US` — it has to be listed explicitly or a viewer who *is* in coverage
// (`PR` is in US_REGION_CODES, and `san-juan--pr` searches normally) would be
// told we can't search their location. The other US territories are absent for
// the same reason they're absent from US_REGION_CODES: the providers API can't
// resolve them to a centroid.
//
// Exported for the search form's US/intl classification, which faces the same
// PR trap (see locationSlugForSearch).
export const IN_COVERAGE_COUNTRIES = new Set(['US', 'PR'])

// True only when geo resolved *and* names a country we have no provider data
// for. An absent/blank country (CloudFront headers missing, pre-fetch render) is
// "unknown", not "outside" — the notice must never fire on a viewer whose geo
// simply didn't resolve. Country is the only signal used: a viewer with a blank
// subdivision code (see formatGeoLabel) is still in coverage.
//
// Home page only, where the question is about the viewer's own location. The
// results page answers the same question from its :location path segment via
// isUsRegionCode — see the cache-key note in results.tsx's loader for why that
// loader must never read geo.
export const isOutsideUs = (geo: GeoLocation | undefined): boolean => {
  const country = geo?.country?.trim()
  if (country == null || country === '') return false
  return !IN_COVERAGE_COUNTRIES.has(country.toUpperCase())
}
